import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeft, FileText, MapPin, Search } from 'lucide-react';
import { api } from '../../services/api';
import GreetedContactButtons from '../components/GreetedContactButtons';
import Disclosure from '../components/Disclosure';
import NotesThread from '../components/NotesThread';
import { countryName, formatDate, formatDateTime, formatMoney, maskedPhone } from '../format';
import '../crm.css';

export default function CrmClientPage() {
  const { t, i18n } = useTranslation('common');
  const { clientId } = useParams();
  const [detail, setDetail] = useState(null);
  const [meta, setMeta] = useState(null);
  const [error, setError] = useState('');
  const [picker, setPicker] = useState(null);
  const [pickerQuery, setPickerQuery] = useState('');
  const [fields, setFields] = useState({ name: '', city: '', company: '' });
  // The whole attached set is sent at once, so a second click while the first is
  // in flight would compute its list from the old one and undo it.
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const data = await api.getCrmClient(clientId);
      setDetail(data);
      setFields({ name: data.client.name, city: data.client.city, company: data.client.company });
    } catch (e) {
      setError(e?.response?.data?.detail || e.message);
    }
  }, [clientId]);

  useEffect(() => {
    api.getCrmMeta().then(setMeta).catch((e) => setError(e?.response?.data?.detail || e.message));
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const openPicker = async () => {
    setPicker(await api.searchCrmDocuments(''));
  };

  const searchDocuments = async (event) => {
    event.preventDefault();
    setPicker(await api.searchCrmDocuments(pickerQuery));
  };

  const toggleDocument = async (documentId) => {
    const attached = detail.client.document_ids;
    const next = attached.includes(documentId)
      ? attached.filter((id) => id !== documentId)
      : [...attached, documentId];
    setSaving(true);
    try {
      setDetail(await api.setCrmClientDocuments(clientId, next));
    } finally {
      setSaving(false);
    }
  };

  if (error) return <div className="crm-page"><div className="crm-error">{String(error)}</div></div>;
  if (!detail) return <div className="crm-page"><div className="spinner" /></div>;

  const { client, leads, documents } = detail;
  const where = [client.city, countryName(client.region, i18n.language)].filter(Boolean).join(', ');
  const paidValue = leads.reduce((sum, lead) => sum + lead.paid_total, 0);
  const owed = leads.reduce((sum, lead) => sum + (lead.amount_due === null ? 0 : lead.amount_due), 0);

  return (
    <div className="crm-page crm-detail crm-client-page">
      <Link className="crm-back" to="/crm/clients">
        <ArrowLeft size={15} />
        {t('crm.client.backToClients')}
      </Link>

      <header className="crm-hero">
        <div className="crm-hero-identity">
          <h1 className="crm-hero-name">{client.name}</h1>
          <div className="crm-chips">
            {client.company && <span className="crm-chip">{client.company}</span>}
            {where && (
              <span className="crm-chip">
                <MapPin size={12} aria-hidden="true" />
                {where}
              </span>
            )}
            {client.tags.map((tag) => (
              <span className="crm-chip" key={tag}>{tag}</span>
            ))}
          </div>
        </div>
      </header>

      <div className="crm-vitals">
        <div className={`crm-vital${owed > 0 ? ' owing' : ''}`}>
          <span className="crm-vital-label">{t('crm.client.owes')}</span>
          <strong className="crm-vital-value">
            {formatMoney(owed, i18n.language)}
            <span className="crm-vital-unit">{t('crm.currency')}</span>
          </strong>
          <span className="crm-vital-note">
            {owed > 0 ? t('crm.client.owesNote') : t('crm.payment.paid')}
          </span>
        </div>
        <div className="crm-vital">
          <span className="crm-vital-label">{t('crm.client.lifetimeValue')}</span>
          <strong className="crm-vital-value">
            {formatMoney(paidValue, i18n.language)}
            <span className="crm-vital-unit">{t('crm.currency')}</span>
          </strong>
          <span className="crm-vital-note">{t('crm.client.since', { when: formatDate(client.created_at) })}</span>
        </div>
        <div className="crm-vital">
          <span className="crm-vital-label">{t('crm.client.enquiries')}</span>
          <strong className="crm-vital-value">{leads.length}</strong>
          <span className="crm-vital-note">
            {t('crm.client.translationCount', { count: documents.length })}
          </span>
        </div>
      </div>

      <div className="crm-contact-dock">
        {client.phones.map((phone) => (
          <GreetedContactButtons
            key={phone.e164}
            record={client}
            fill={(id) => api.fillCrmClientGreetingNames(id)}
            greetingScript={meta?.greeting_script}
            phone={phone}
          />
        ))}
        {client.phones.length === 0 && client.emails.length > 0 && (
          <GreetedContactButtons
            record={client}
            fill={(id) => api.fillCrmClientGreetingNames(id)}
            greetingScript={meta?.greeting_script}
            email={client.emails[0]}
          />
        )}
      </div>

      <div className="crm-detail-grid">
        <div className="crm-detail-main">
          <section className="crm-card" data-area="quote">
            <div className="crm-card-head">
              <h2>{t('crm.client.translations')}</h2>
              <button type="button" className="crm-btn" onClick={openPicker}>
                <FileText size={15} />
                {t('crm.client.attach')}
              </button>
            </div>
            {documents.length === 0 ? (
              <p className="crm-empty-line">{t('crm.client.noTranslations')}</p>
            ) : (
              <ul className="crm-doc-list">
                {documents.map((doc) => (
                  <li key={doc.id}>
                    <span>
                      {doc.chat_id ? (
                        <Link className="crm-link" to={`/chat/${doc.chat_id}`}>{doc.original_filename}</Link>
                      ) : (
                        doc.original_filename
                      )}
                    </span>
                    <span className="crm-cell-sub">
                      {doc.language_from || '?'} → {doc.language_to} · {doc.char_count ?? '—'} · {formatDate(doc.created_at)}
                    </span>
                    <button
                      type="button"
                      className="crm-icon-btn"
                      disabled={saving}
                      onClick={() => toggleDocument(doc.id)}
                    >
                      {t('crm.client.detach')}
                    </button>
                  </li>
                ))}
              </ul>
            )}

            {picker && (
              <div className="crm-picker">
                <form className="crm-searchbar" onSubmit={searchDocuments}>
                  <Search size={15} />
                  <input
                    className="crm-search-input"
                    value={pickerQuery}
                    onChange={(event) => setPickerQuery(event.target.value)}
                    placeholder={t('crm.client.searchTranslations')}
                  />
                  <button type="submit" className="crm-btn">{t('crm.search')}</button>
                  <button type="button" className="crm-btn" onClick={() => setPicker(null)}>{t('crm.close')}</button>
                </form>
                {picker.length === 0 ? (
                  <p className="crm-empty-line">{t('crm.client.noneFound')}</p>
                ) : (
                  <ul className="crm-picker-list">
                    {picker.map((doc) => (
                      <li key={doc.id}>
                        <label>
                          <input
                            type="checkbox"
                            checked={client.document_ids.includes(doc.id)}
                            disabled={saving}
                            onChange={() => toggleDocument(doc.id)}
                          />
                          {doc.original_filename}
                          <span className="crm-cell-sub">
                            {doc.language_from || '?'} → {doc.language_to} · {formatDate(doc.created_at)}
                          </span>
                        </label>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            )}
          </section>

          <section className="crm-card" data-area="work">
            <h2>{t('crm.client.enquiries')}</h2>
            {leads.length === 0 ? (
              <p className="crm-empty-line">{t('crm.client.noEnquiries')}</p>
            ) : (
              <ul className="crm-history">
                {leads.map((lead) => (
                  <li key={lead.id}>
                    <span className={`crm-status-dot crm-status-${lead.status}`} />
                    <Link className="crm-link" to={`/crm/leads/${lead.id}`}>
                      {t(`crm.status.${lead.status}`)} · {formatDateTime(lead.created_at)}
                    </Link>
                    {lead.attribution.form_path && (
                      <span className="crm-cell-sub crm-break">{lead.attribution.form_path}</span>
                    )}
                    <span className={`crm-pay-badge crm-pay-${lead.payment_state}`}>
                      {t(`crm.payment.${lead.payment_state}`)}
                    </span>
                    {lead.order_total !== null && (
                      <strong className="crm-history-value">
                        {formatMoney(lead.order_total, i18n.language)} {t('crm.currency')}
                      </strong>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </section>

          <section className="crm-card" data-area="notes">
            <h2>{t('crm.notes.title')}</h2>
            <NotesThread notes={client.notes} onAdd={(text) => api.addCrmClientNote(clientId, text).then(load)} />
          </section>
        </div>

        <aside className="crm-detail-rail">
          <Disclosure area="client" title={t('crm.client.details')} summary={where || client.company}>
            <label className="crm-field">
              <span className="crm-field-label">{t('crm.client.name')}</span>
              <input
                className="crm-input"
                value={fields.name}
                onChange={(event) => setFields({ ...fields, name: event.target.value })}
                onBlur={() => api.patchCrmClient(clientId, { name: fields.name }).then(load)}
                maxLength={120}
              />
            </label>
            <label className="crm-field">
              <span className="crm-field-label">{t('crm.client.city')}</span>
              <input
                className="crm-input"
                value={fields.city}
                onChange={(event) => setFields({ ...fields, city: event.target.value })}
                onBlur={() => api.patchCrmClient(clientId, { city: fields.city }).then(load)}
                maxLength={120}
              />
            </label>
            <label className="crm-field">
              <span className="crm-field-label">{t('crm.manual.company')}</span>
              <input
                className="crm-input"
                value={fields.company}
                onChange={(event) => setFields({ ...fields, company: event.target.value })}
                onBlur={() => api.patchCrmClient(clientId, { company: fields.company }).then(load)}
                maxLength={200}
              />
            </label>
          </Disclosure>

          <section className="crm-card crm-quiet" data-area="origin">
            <h2>{t('crm.client.contacts')}</h2>
            <dl className="crm-defs">
              <dt>{t('crm.manual.phone')}</dt>
              <dd>{client.phones.map(maskedPhone).join(', ') || '—'}</dd>
              <dt>{t('crm.manual.email')}</dt>
              <dd className="crm-break">{client.emails.join(', ') || '—'}</dd>
              <dt>{t('crm.client.where')}</dt>
              <dd>{where || '—'}</dd>
            </dl>
          </section>
        </aside>
      </div>
    </div>
  );
}
